import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { draftBroadcast, uploadFiles } from 'api/broadcasts.service';
import { useToast } from 'hooks/ui';
import { Broadcast } from 'models/broadcast.model';
import { isLocalFile } from 'interfaces';
import { scrollToItem } from 'hooks/helpers/common';
import { useApplicationStore } from 'store/application';
import { broadcastRoutes } from 'router/broadcastRoutes';
import { broadcastKeys } from './queryKeys';

type HookArgs = {
  keepActive?: boolean;
};

export const useBroadcastCreateQuery = (
  props: HookArgs = { keepActive: false },
) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setLoading, removeLoading } = useApplicationStore((state) => state);

  const { isPending, mutate, data } = useMutation({
    mutationFn: async (broadcast: Broadcast) => {
      setLoading();

      return draftBroadcast({
        title: broadcast.title,
        message: broadcast.message,
        tagIds: broadcast.tagIds,
        userRoles: broadcast.userRoles,
        schedule: broadcast.schedule?.toISOString() ?? '',
      }).then(async (res) => {
        const files = broadcast.attachments?.filter(isLocalFile);

        if (files?.length) {
          await uploadFiles(res.id, files);
        }

        return res;
      });
    },
    onSuccess: (broadcast: Broadcast) => {
      queryClient.invalidateQueries({ queryKey: broadcastKeys.all });
      toast.success('Broadcast has been drafted successfully');

      if (props.keepActive) {
        navigate(broadcastRoutes.broadcastId(broadcast.id));
        scrollToItem(broadcast.id);
      }
    },
    onError: (error: AxiosError) => toast.error(error.message),
    onSettled: () => removeLoading(),
  });

  return { isLoading: isPending, mutate, data };
};
